import {
  Button,
  Cell,
  StandaloneLink,
  TextInput,
} from '@zeiss/beyond-online-react';
import {
  Box,
  ButtonGroup,
  Grid,
  ReadOnlyField,
  Section,
  SectionActionCell,
  SectionHeadline,
  SectionText,
} from '@zeiss/lib-components';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext, useParams } from 'react-router-dom';

import { MAX_CHARS_FOR_TENANT_NAME } from 'src/constants';
import { showToast } from 'src/helpers/showToast';
import { useEditTenant } from 'src/hooks/api/useEditTenant';
import {
  getOrganizationInfo,
  OrganizationSearch,
} from 'src/modules/TenantManagement/components/OrganizationSearch';
import {
  DeleteTenantModal,
  getDetailsIsChanged,
  OutletContextType,
  TenantDetailsState,
} from 'src/modules/TenantManagement/Tenant';
import { isTenantValid } from 'src/modules/TenantManagement/TenantCreate/utils';
import { StandardRouteParams } from 'src/types';
import { OrganizationResponse } from 'src/types/rest/tenant';

export const Details = () => {
  const { t } = useTranslation(['common']);

  const { applicationListId } = useParams<StandardRouteParams>();

  const {
    canDelete,
    canUpdateName,
    canUpdateOrganization,
    data: { id, managementPolicy, managers = [], name, organization = null },
  } = useOutletContext<OutletContextType>();

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] =
    React.useState(false);

  const [details, setDetails] = React.useState<TenantDetailsState>({
    managementPolicy,
    managers: managers ?? [],
    name,
    organization,
  });

  const { isPending, mutate: editTenant } = useEditTenant(id);

  const handleTenantOrganization = (
    organization: OrganizationResponse | null,
  ) => {
    setDetails((prev) => ({
      ...prev,
      organization,
    }));
  };

  const handleTenantNameChange: React.ChangeEventHandler<HTMLInputElement> = ({
    target: { value: name },
  }) => {
    setDetails((prev) => ({
      ...prev,
      error: '',
      name,
    }));
  };

  const showConfirmationModal = () => {
    setIsConfirmationModalOpen(true);
  };

  const closeConfirmationModal = () => {
    setIsConfirmationModalOpen(false);
  };

  const cancel = () => {
    setDetails({
      managementPolicy,
      managers: managers ?? [],
      name,
      organization,
    });
  };

  const save = () => {
    const { managers, name, organization } = details;
    const { customerNumber } = organization ?? {};

    editTenant(
      {
        managerIds: managers?.map(({ id }) => id),
        name,
        organizationCustomerNumber: customerNumber,
      },
      {
        onError: (error) => {
          if (error instanceof Response && error.status === 422) {
            return setDetails((prev) => ({
              ...prev,
              error: t('error.tenantExists'),
            }));
          }

          showToast({
            props: {
              title: t('common:notification.errorX', {
                action: t('common:action.updated'),
                x: t('common:glossary.tenant'),
              }),
              type: 'error',
            },
          });
        },
        onSuccess: () => {
          showToast({
            props: {
              title: t('common:notification.successX', {
                action: t('common:action.updated'),
                x: t('common:glossary.tenant'),
              }),
              type: 'success',
            },
          });
        },
      },
    );
  };

  const existingDetails: Omit<TenantDetailsState, 'managementPolicy'> = {
    managers: managers ?? [],
    name,
    organization,
  };

  const hasUnsavedChanges = getDetailsIsChanged({
    current: details,
    initial: existingDetails,
  });

  const { error } = details;

  const nameLabel = t('common:label.xName', {
    x: t('common:glossary.tenant'),
  });

  const organizationReadOnlyValue = getOrganizationInfo(details.organization);

  return (
    <>
      <Section>
        <Cell.Basic colSpan={[4, 6, 4]}>
          <SectionHeadline headline={t('common:title.general')} size="s" />
        </Cell.Basic>

        <Cell.Basic colSpan={[4, 6, 8]}>
          <Box>
            <Grid>
              <Cell.Basic colSpan={[4, 6, 6]}>
                {canUpdateName ? (
                  <TextInput
                    autoComplete="off"
                    error={error ? error : undefined}
                    helperText={t('common:input.tenantNameHelperText')}
                    label={nameLabel}
                    maxLength={MAX_CHARS_FOR_TENANT_NAME}
                    onChange={handleTenantNameChange}
                    value={details.name}
                  />
                ) : (
                  <ReadOnlyField text={details.name} title={nameLabel} />
                )}
              </Cell.Basic>

              <Cell.Basic colSpan={[4, 6, 6]} data-testid="organizationCell">
                {canUpdateOrganization ? (
                  <OrganizationSearch
                    handleOrganizationSelect={handleTenantOrganization}
                    selectedOrganization={details.organization}
                  />
                ) : (
                  <ReadOnlyField title={t('common:glossary.organization')}>
                    {organizationReadOnlyValue}
                  </ReadOnlyField>
                )}
              </Cell.Basic>
            </Grid>
          </Box>

          {hasUnsavedChanges ? (
            <ButtonGroup spacing="s" variant="form">
              <Button onClick={cancel} variant="secondary">
                {t('common:action.cancel')}
              </Button>
              <Button
                disabled={!isTenantValid(details)}
                loading={isPending}
                onClick={save}
              >
                {t('common:action.saveChanges')}
              </Button>
            </ButtonGroup>
          ) : null}
        </Cell.Basic>
      </Section>

      {canDelete ? (
        <Section>
          <Cell.Basic colSpan={[4, 6, 4]}>
            <SectionHeadline
              headline={t('common:action.deleteX', {
                x: t('common:glossary.tenant'),
              })}
              size="s"
            />

            <SectionText>
              {t('common:text.deleteXhere', {
                x: t('common:glossary.tenant'),
              })}
            </SectionText>
          </Cell.Basic>

          <SectionActionCell colSpan={[4, 6, 8]}>
            <Box>
              <StandaloneLink href="" onClick={showConfirmationModal} size="xs">
                {t('common:action.deleteX', {
                  x: name,
                })}
              </StandaloneLink>
            </Box>
          </SectionActionCell>
        </Section>
      ) : null}

      <DeleteTenantModal
        applicationListId={applicationListId}
        data={{ id, name }}
        isOpen={isConfirmationModalOpen}
        onClose={closeConfirmationModal}
      />
    </>
  );
};
